import { CbStatus } from '../redux/modules/conversation/types';
import dayjs from 'dayjs';

export class EvaluationHelper {
  constructor() {
    this.head = null;
    this.tail = null;
  }

  getHead() {
    return this.head;
  }

  setHead(head) {
    this.head = head;
  }

  getStatus(block) {
    if (block && !block.isAttemptable) {
      return CbStatus.NOT_APPLICABLE;
    }
    if (block && block.userResponse && block.userResponse.length > 0) {
      return CbStatus.ANSWERED;
    }
    return CbStatus.NOT_ATTEMPTED;
  }

  isEvaluationCompleted() {
    let block = this.head;
    while (block) {
      if (block.fullPage && block.evaluationResponses.length === 0) {
        return false;
      } else {
        return true;
      }
      block = block.next;
    }
    return true;
  }

  createNode(block) {
    const isDct = block.basicInfo.type === 'dct';
    return {
      next: null,
      status: this.getStatus(block),
      text: block?.basicInfo.text,
      subtitle: block.subtitle,
      cbId: block.cbId,
      cbType: block.basicInfo.type,
      required: block.required,
      dctId: block.dctId,
      isAttemptable: block.isAttemptable,
      forceManualEntry: block.forceManualEntry,
      fullPage: isDct ? block.fullPage : false,
      usePreviousResponses: block.usePreviousResponses,
      responderType: block.responderType || 'MEMBER',
      cbProfileElementDetails: block.cbProfileElementDetails || [],
      cbRelatedProfileElements: block?.relatedProfileElements || [],
      choices: block.basicInfo.choices || [],
      children: [],
      userResponse: block.userResponse || null,
      helperText: block.helperText,
      evaluationResponses: block.userResponse?.userResponseList || [],
      prompt: block.prompt,
      ratingScale: block.basicInfo.ratingScale || null,
      educationContentSlug: block.basicInfo.educationContentSlug,
      exerciseContent: block.basicInfo.exerciseContent,
    };
  }

  addNode(block) {
    const node = this.createNode(block);
    if (!this.head) {
      node.isFirst = true;
      this.head = node;
      this.tail = node;
    } else {
      this.tail.next = node;
      this.tail = this.tail.next;
    }
    return node;
  }

  updateValues(payload, evaluationHead) {
    const { id, value, narrative, blockId, isUse } = payload;
    this.setHead(evaluationHead);
    const node = this.getContentBlock(id);

    const updateNodeResponses = (block, blockValue, blockNarrative) => {
      if (block.userResponse) {
        if (block.cbType === 'text-input') {
          if (blockNarrative || blockNarrative === '') {
            block.userResponse.providerNarrative = blockNarrative;
          }
          if (blockValue || blockValue === '') {
            const response = blockValue !== '' ? [blockValue] : [];

            block.userResponse.userResponseList = response;
            block.evaluationResponses = response;
          }
        } else {
          const { userResponseList } = block.userResponse;
          if (isUse && block.cbType === 'multi-select') {
            userResponseList.length = 0;
          }
          if (blockNarrative || blockNarrative === '') {
            block.userResponse.providerNarrative = blockNarrative;
          } else {
            const valueIndex = userResponseList.indexOf(blockValue);
            if (valueIndex !== -1) {
              userResponseList.splice(valueIndex, 1);
            } else if (block.cbType === 'single-select' || block.cbType === 'rating-scale') {
              block.userResponse.userResponseList = [blockValue];
              block.evaluationResponses = [blockValue];
            } else {
              userResponseList.push(blockValue);
              block.evaluationResponses?.push(blockValue);
            }
          }
        }
      } else if (block.cbType === 'education' || block.cbType === 'exercise') {
        block.userResponse = {
          userResponseList: blockValue,
          providerNarrative: blockNarrative,
          score: 0,
        };
        block.evaluationResponses = blockValue;
      } else {
        block.userResponse = {
          userResponseList: blockValue ? [blockValue] : [],
          providerNarrative: blockNarrative || '',
          score: 0,
        };
        block.evaluationResponses = blockValue ? [blockValue] : [];
      }
    };

    if (node.cbType === 'dct' && node.fullPage) {
      let response = null;
      node.children.forEach(child => {
        if (child.cbId === blockId) {
          updateNodeResponses(child, value, narrative);
          response = {
            cbId: child.cbId,
            providerNarrative: child.userResponse?.providerNarrative || '',
            evaluationResponses: child.userResponse?.userResponseList || [],
          };
        }
      });
      if (response) {
        // Check if response already exists for cb
        const existingResponseIndex = node.evaluationResponses.findIndex(res => res.cbId === blockId);
        if (existingResponseIndex !== -1) {
          // Update the existing response
          node.evaluationResponses[existingResponseIndex] = response;
        }
        // add a new response
        else node.evaluationResponses.push(response);
      }
    } else updateNodeResponses(node, value, narrative);
  }

  evaluateDisplayLogic(cb, expandedEvaluation, profileElements) {
    const allCBs = expandedEvaluation.contentBlocks;
    const displayLogic = cb.displayLogics;
    let ruleAggregator = 'and';

    if (cb?.ruleAggregator) ruleAggregator = cb.ruleAggregator.toLowerCase();

    if (!Array.isArray(displayLogic) || displayLogic.length === 0) {
      return true;
    }

    const evaluateRule = ({ type, key, rule, value }) => {
      let mapping;
      // always display
      if (type === '' || type === 'always') return true;

      // related content block
      if (type === 'R') {
        const relatedCB = allCBs.find(item => item.cbId === key);
        if (relatedCB) {
          const response = relatedCB.userResponse;
          mapping = Array.isArray(response?.userResponseList) ? response.userResponseList : response;
        }

        if (mapping == null) return false;

        const getFirstValue = () => (Array.isArray(mapping) ? mapping[0] : mapping);
        if (rule === 'contains') {
          return Array.isArray(mapping)
            ? mapping.includes(value)
            : typeof mapping === 'string' && mapping.includes(value);
        } else if (rule === 'equals') {
          return getFirstValue() === value;
        } else if (rule === 'not_equals') {
          return getFirstValue() !== value;
        } else if (rule === 'in') {
          return Array.isArray(value) && mapping.includes(value);
        } else if (rule === 'not_in') {
          return Array.isArray(value) && !mapping.includes(value);
        } else if (rule === 'exists') {
          return getFirstValue() != null;
        } else if (rule === 'not_exists') {
          return getFirstValue() == null;
        } else {
          return false;
        }
      }

      // profile element
      if (type === 'P') {
        const profileElement = profileElements[key];

        if (!profileElement) {
          return false;
        }
        if (rule === 'contains' || rule === 'equals') {
          return profileElement.includes(value);
        } else if (rule === 'not-equals' || rule === 'not_in') {
          return !profileElement.includes(value);
        } else {
          return false;
        }
      }
    };

    return ruleAggregator === 'or' ? displayLogic.some(evaluateRule) : displayLogic.every(evaluateRule);
  }

  transformEvaluation(expandedEvaluation, profileElements) {
    this.head = null;
    this.tail = null;
    if (!expandedEvaluation.contentBlocks) {
      return;
    }
    expandedEvaluation.contentBlocks.forEach(cb => {
      cb.isAttemptable = this.evaluateDisplayLogic(cb, expandedEvaluation, profileElements);
      if (cb.isAttemptable) {
        const filteredCBs = expandedEvaluation.contentBlocks.filter(item => item.cbId === cb.cbId);
        if (filteredCBs[0]?.basicInfo?.questionFrequency) {
          cb.isAttemptable = this.evaluateQuestionFrequency(filteredCBs[0]);
        }
      }
      if (cb.basicInfo.type === 'dct') {
        const dct = expandedEvaluation.dcts.find(d => d.dctId === cb.dctId);
        cb.basicInfo.text = dct.name;
        cb.subtitle = dct.description;
        cb.fullPage = dct.fullPage;
        const dctNode = this.addNode(cb);
        dct.contentBlocks.forEach(cbId => {
          const childItem = expandedEvaluation.contentBlocks.find(block => block.cbId === cbId);
          if (childItem) {
            childItem.isAttemptable = this.evaluateDisplayLogic(
              childItem,
              expandedEvaluation,
              profileElements,
            );
            if (childItem.isAttemptable) {
              const filteredCBs = expandedEvaluation.contentBlocks.filter(
                item => item.cbId === childItem.cbId,
              );
              if (filteredCBs[0]?.basicInfo?.questionFrequency) {
                childItem.isAttemptable = this.evaluateQuestionFrequency(filteredCBs[0]);
              }
            }
            if (!dct.fullPage) {
              const childNode = this.addNode(childItem);
              dctNode.children.push(childNode);
            } else {
              dctNode.children.push(this.createNode(childItem));
            }
          }
        });
      } else if (!cb.dctId) {
        this.addNode(cb);
      }
    });
  }

  getNextApplicableBlock(cbId, onlyUnanswered) {
    let currentBlock = this.getContentBlock(cbId);
    while (currentBlock) {
      if (currentBlock.next && currentBlock.next.isAttemptable) {
        if (onlyUnanswered) {
          if (
            (currentBlock.next.cbType === 'dct' || currentBlock.next.status !== CbStatus.ANSWERED) &&
            currentBlock.next.cbType !== 'education' &&
            currentBlock.next.cbType !== 'exercise'
          ) {
            return currentBlock.next;
          }
        } else {
          return currentBlock.next;
        }
      }
      currentBlock = currentBlock.next;
    }
    return null;
  }

  getPreviousApplicableBlock(cbId, onlyUnanswered) {
    let currentBlock = this.getPreviousNode(cbId);
    while (currentBlock) {
      if (currentBlock.isAttemptable) {
        if (onlyUnanswered) {
          if (
            currentBlock.status !== CbStatus.ANSWERED &&
            currentBlock.cbType !== 'education' &&
            currentBlock.cbType !== 'exercise'
          ) {
            return currentBlock;
          }
        } else {
          return currentBlock;
        }
      }
      currentBlock = this.getPreviousNode(currentBlock.cbId);
    }
    return null;
  }

  getContentBlock(cbId) {
    let current = this.head;
    while (current) {
      if (current.cbId === cbId) {
        return current;
      }
      current = current.next;
    }
    return null;
  }

  getNextDct(cbId) {
    let current = this.getContentBlock(cbId);
    while (current) {
      current = current.next;
      if (current && current.cbType === 'dct') {
        return current;
      }
    }
    return null;
  }

  getDcts() {
    const sections = [];
    let current = this.head;
    while (current) {
      if (current.cbType === 'dct') {
        sections.push(current);
      }
      current = current.next;
    }
    return sections;
  }

  getQuestions() {
    const sections = [];
    let current = this.head;
    while (current) {
      if (current.cbType === 'dct' || !current.dctId) {
        sections.push(current);
      }
      current = current.next;
    }
    return sections;
  }

  getFilteredQuestions() {
    const sections = [];
    let current = this.head;
    while (current) {
      if (current.cbType !== 'dct') {
        sections.push(current);
      }
      current = current.next;
    }
    return sections;
  }

  getPreviousNode(cbId) {
    let current = this.head;
    while (current) {
      if (current.next && current.next.cbId === cbId) {
        return current;
      }
      current = current.next;
    }
    return null;
  }

  getContentBlocksByDct(dctId) {
    const blocks = [];
    let current = this.head;
    while (current) {
      if (current.dctId === dctId && current.cbType !== 'dct') {
        blocks.push(current);
      }
      current = current.next;
    }
    return blocks;
  }

  skipNonRequiredCB(cbId) {
    const block = this.getContentBlock(cbId);
    if (block) {
      block.status = CbStatus.SKIPPED;
    }
  }

  evaluateQuestionFrequency(contentBlock) {
    const questionFrequency = contentBlock?.basicInfo?.questionFrequency;
    const frequencyCount = parseInt(contentBlock?.basicInfo?.frequencyCount, 10);
    const temporalResponses = contentBlock?.temporalResponses;

    switch (questionFrequency) {
      case 'ONE_TIME':
        return !temporalResponses || temporalResponses?.length === 0;
      case 'EVERY_TIME':
        return true;
      case 'DAYS': {
        if (!temporalResponses || temporalResponses?.length === 0) {
          return true;
        }
        const lastResponse = temporalResponses
          .filter(r => r.answeredAt)
          .sort((a, b) => dayjs(b.answeredAt).diff(dayjs(a.answeredAt)))[0];

        if (!lastResponse) return true;

        const hoursSinceLast = dayjs().diff(dayjs(lastResponse.answeredAt), 'hour');
        const requiredHours = frequencyCount * 24;

        return hoursSinceLast >= requiredHours;
      }
      case 'APPOINTMENTS': {
        if (contentBlock.appointmentsSinceLastResponse === -1) {
          // This means question is never answered. Need to ask this question
          return true;
        }
        return (
          contentBlock.appointmentsSinceLastResponse >= parseInt(contentBlock?.basicInfo?.frequencyCount, 10)
        );
      }
      default:
        return true;
    }
  }
}
